import React from 'react';
import DocumentPage, {
  Section,
  SubSection,
  Paragraph,
  BulletList,
  OrderedList,
  DoDoNot,
  HazardTable,
} from './DocumentPage';

const ForkLiftTruckDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Fork Lift Truck – RA & SSOW"
    documentHeading="Risk Assessment & Safe System of Work: Fork Lift Truck Operations"
    meta={[
      ['Document Reference', 'PVS-RAMS-FLT-002'],
      ['Version', '2.0'],
      ['Date of Issue', '17th March 2026'],
      ['Next Review Date', '17th March 2028'],
      ['Area / Location', 'Company Premises – Workshops / Yard areas'],
      ['Activity', 'All operations involving the use of forklift trucks (movement of LPG tanks and other items, loading / unloading, general warehouse / yard operations)'],
      ['Review Period', '24 months'],
    ]}
    acknowledgment={{
      documentName: 'Fork Lift Truck – RA & SSOW (PVS-RAMS-FLT-002)',
      documentReference: 'PVS-RAMS-FLT-002',
      processActivity: 'Fork Lift Truck Operations',
      versionNumber: '2.0',
      dateOfIssue: '17th March 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <Section number="1" title="Introduction and Scope">
      <Paragraph>
        This combined Risk Assessment and Safe System of Work is designed for use by Pressure Vessel
        Services Limited employees when operating forklift trucks at the company's premises. This
        includes, but is not limited to, loading and unloading of vessels, moving materials and
        general warehouse / yard operations. It aims to identify potential hazards and outline
        control measures to ensure compliance with the Health and Safety at Work Act 1974, the
        Provision and Use of Work Equipment Regulations 1998 (PUWER), the Lifting Operations and
        Lifting Equipment Regulations 1998 (LOLER), and other relevant UK legislation.
      </Paragraph>
      <Paragraph className="mt-4">
        There are reputed to be over 8,000 reported accidents involving the use of forklift trucks
        in the UK each year and some of these result in fatalities. The majority are caused by lack
        of sufficient training, operator error, lack of knowledge about the equipment and working
        environment, bad truck maintenance, poor lighting, inadequate gangways and unsuitable
        premises. Slightly more pedestrians are injured as the result of a forklift accident than
        operators themselves.
      </Paragraph>
      <Paragraph className="mt-4 font-semibold">
        This document is a general assessment for routine forklift operations. Employees are
        responsible for conducting a visual inspection prior to commencing work for any additional
        dangers or unforeseen risks. Risks are not limited to those listed below, and if it is the
        feeling of the employees that a particular job requires a further detailed, custom risk
        assessment, this shall be done and approved by a Senior Engineer or Management.
      </Paragraph>
    </Section>

    <Section number="2" title="General Information">
      <BulletList
        items={[
          <><strong>Company:</strong> Pressure Vessel Services Limited</>,
          <><strong>Location:</strong> Company premises (fabrication shed, engineering workshop, yard).</>,
          <><strong>Activity:</strong> All operations involving the use of forklift trucks.</>,
          <><strong>Key Personnel:</strong> Trained and certified Forklift Operators.</>,
          <><strong>Equipment:</strong> Forklift trucks, attachments (if any), lifting accessories (chains, slings).</>,
          <>
            <strong>Reference Documents:</strong>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>PVS Health and Safety Policy 2025</li>
              <li>Written Scheme of Examination 2024 (relevant for vessel handling)</li>
              <li>Method Statement: Lifting Operations and Transport of LPG Storage Vessels</li>
              <li>Method Statement: Pyrolysis of LPG Storage Vessels</li>
              <li>Method Statement: Shot Blasting of LPG Storage Vessels</li>
              <li>Manufacturer's Operating Manual for Forklift Truck</li>
            </ul>
          </>,
        ]}
      />
    </Section>

    <Section number="3" title="Responsibilities">
      <Paragraph><strong>Overall Responsibility:</strong> The Board of Directors of the Employer.</Paragraph>
      <Paragraph className="mt-2"><strong>Day-to-Day Responsibility:</strong> Health and Safety Officer (Senior Engineer on duty).</Paragraph>

      <SubSection title="Forklift Operator Responsibilities">
        <BulletList
          items={[
            'Hold valid certification / licence for the type of forklift being operated.',
            'Conduct daily pre-use checks of the forklift and report any defects immediately.',
            "Operate the forklift in accordance with training, company procedures, and manufacturer's instructions.",
            "Ensure loads are stable, correctly positioned, and within the forklift's capacity.",
            'Maintain clear visibility, especially when travelling with a load.',
            'Adhere to designated routes, speed limits and traffic management plans.',
            'Give way to pedestrians and ensure clear separation where possible.',
            'Report all health and safety concerns, incidents or near misses promptly to the Health and Safety Officer.',
            'Conduct a visual inspection of the work area prior to commencing work for any additional dangers or unforeseen risks.',
            'Notify the on-duty Senior Engineer or management immediately of any unforeseen risks or hazards that require further assessment or a custom RAMS.',
          ]}
        />
      </SubSection>

      <SubSection title="Other Employee Responsibilities (Working Near Forklifts)">
        <BulletList
          items={[
            'Be aware of forklift movements and designated operating areas.',
            'Maintain safe distances from operating forklifts.',
            'Wear appropriate PPE (Hi-vis, safety footwear).',
            'Do not distract forklift operators.',
            'Report any unsafe forklift operation or conditions.',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="4" title="Hazard Identification and Risk Assessment">
      <HazardTable
        columns={['Hazard', 'Potential Harm', 'Existing Controls', 'Residual Risk']}
        rows={[
          [
            'Forklift Overturning',
            'Crushing, serious injury, fatality to operator or bystanders.',
            [
              'Operators are trained and certified.',
              'Loads within rated capacity and load centre.',
              'Loads carried as low as possible.',
              'Avoid sudden turns, braking or acceleration.',
              'Slow down on ramps / inclines.',
              'Avoid driving over uneven surfaces or potholes.',
              'Seatbelt worn by operator.',
            ],
            'Low',
          ],
          [
            'Collision with Pedestrians / Vehicles / Structures',
            'Crushing, striking, serious injury, property damage.',
            [
              'Designated pedestrian walkways and forklift routes.',
              'Clear visibility maintained (mirrors, lights, horns).',
              'Sounding horn at blind spots / intersections.',
              'Speed limits enforced.',
              'Operators give way to pedestrians.',
              'Good lighting in work areas.',
              'Secure work area (barriers, cones) where necessary.',
              'Signage is sited at strategic locations "Warning – Fork Lift Trucks".',
              'Hi-viz is worn by all employees on site.',
            ],
            'Low',
          ],
          [
            'Dropped Load',
            'Crushing, striking, property damage, injury from LPG release (if vessel damaged).',
            [
              'Loads stable and correctly positioned on forks.',
              'Forks fully under load.',
              'Backrest used where appropriate.',
              'Load within forklift capacity and safe working load is displayed and not exceeded.',
              'Smooth movements during lifting / lowering.',
              'Vessels handled with caution and care to prevent damage.',
              'Designated lifting points / slings used for vessels.',
            ],
            'Low',
          ],
          [
            'Mechanical Failure of Forklift',
            'Loss of control, dropped load, fire.',
            [
              'Daily pre-use checks by operator.',
              'Regular planned maintenance and servicing; evidence of service held on file.',
              'Defects reported immediately and forklift taken out of service.',
              'LOLER inspection for lifting components; evidence of inspection held on file.',
              'Forklift attachments thoroughly examined / inspected to comply with LOLER.',
            ],
            'Low',
          ],
          [
            'Poor Visibility (reversing, high loads)',
            'Collision with personnel / objects.',
            [
              'Travel with load trailing if forward visibility is obstructed.',
              'Use mirrors, lights and horn.',
              'Use a banksman / spotter when visibility is severely restricted.',
              'Good lighting in work areas.',
              'Confirm forklifts are fitted with roll over protection, flashing beacon, restraint system, audible reversing alarm and lights.',
            ],
            'Low',
          ],
          [
            'Manual Handling (attaching chains / slings)',
            'Back injuries, strains, sprains.',
            [
              'Minimise manual handling where possible.',
              'Training on correct lifting techniques.',
              'Assess load before lifting.',
              'Team lifts for heavy / awkward lifting accessories.',
            ],
            'Low',
          ],
          [
            'Fire / Fuel Leaks (Diesel, Battery, LPG)',
            'Fire, burns, environmental contamination, explosion.',
            [
              'Regular maintenance to prevent leaks.',
              'Immediate reporting of leaks.',
              'Appropriate fire extinguishers available.',
              'Designated refuelling areas (if applicable).',
              'Cylinders changed away from sources of ignition in well ventilated areas.',
              'Keys are not left in an unattended fork lift truck.',
            ],
            'Low',
          ],
          [
            'Lack of Competence / Training',
            'Incorrect operation, accidents, equipment damage, non-compliance.',
            [
              'Only certified / licenced operators permitted to use forklifts; evidence of training held on file.',
              'Regular refresher training.',
              'Competence assessed periodically.',
            ],
            'Low',
          ],
          [
            'Emergency Situation',
            'Injury, fatality, delayed response, inadequate evacuation.',
            [
              'Emergency stop button on forklift.',
              'Operators trained in emergency procedures.',
              'Knowledge of site emergency alarms and assembly points.',
              'Prompt reporting of accidents.',
            ],
            'Low',
          ],
        ]}
      />
    </Section>

    <Section number="5" title="Safe System of Work">
      <SubSection title="5.1 Pre-Operation Checks">
        <OrderedList
          items={[
            <><strong>Operator Competence:</strong> Ensure the operator holds a valid and current forklift operating licence / certification for the specific type of truck being used.</>,
            <>
              <strong>Daily Checks:</strong> Conduct a thorough pre-use inspection of the forklift, including:
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Tyres, brakes, steering.</li>
                <li>Forks, mast, lifting chains (no visible damage or wear).</li>
                <li>Lights, horn, warning beacons.</li>
                <li>Fluid levels (oil, water, fuel / battery charge).</li>
                <li>Seatbelt functionality.</li>
                <li>Report any defects immediately and do not operate a defective forklift.</li>
              </ul>
            </>,
            <>
              <strong>Area Survey:</strong> Before commencing work conduct a visual inspection of the work area, including:
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Clearance for overhead obstructions, doorways and aisles.</li>
                <li>Ground conditions (uneven surfaces, potholes, slopes).</li>
                <li>Presence of pedestrians or other vehicles.</li>
                <li>Adequate lighting.</li>
                <li>All designated pedestrian walkways and forklift routes are clear.</li>
              </ul>
            </>,
          ]}
        />
      </SubSection>

      <SubSection title="5.2 Operating">
        <DoDoNot
          doItems={[
            'Carry out a pre-shift check of the lift truck. Report any defects to your Manager or Supervisor.',
            'Wear operator restraints, where fitted. If the fork lift truck should start to roll over, stay in the cab.',
            'Look all around before moving off.',
            'Look in the direction of travel.',
            'Travel at a speed suitable for the location and the load you are carrying.',
            'Avoid any sudden change in direction, sudden stops or sudden acceleration.',
            'Travel with the forks lowered, but clear of the ground.',
            'Watch out for pedestrians.',
            'Watch out for obstructions.',
            'Avoid sudden stops and violent braking.',
            'Slow down at corners, doorways, and at every danger spot and sound the horn where necessary.',
            'Reverse only when it is safe to do so.',
            'Apply the parking brake when leaving the lift truck.',
            'Face the lift truck and use steps and handholds when getting in or out.',
          ]}
          doNotItems={[
            'Operate a lift truck unless you are trained and authorised to do so.',
            'Use a lift truck or equipment you know is not working properly.',
            'Exceed the safe working load.',
            "Start the fork lift truck unless you are sat in the driver's seat, the parking brake is on and the fork lift truck is in neutral.",
            'Operate controls from outside the cab unless it is designed so you can do this.',
            'Travel on uneven ground unless the lift truck is suitable for this.',
            'Travel forward when the load obscures vision.',
            'Run over unprotected cables or flexible pipes.',
            'Try to carry out repairs – leave this to a qualified maintenance engineer.',
            'Operate a lift truck when under the influence of alcohol or drugs.',
            'Use mobile phones or other hand-held devices while operating or travelling.',
            'Lift a person on the forks, or on a pallet, or similar, balanced on the forks.',
            'Carry passengers, unless the lift truck is designed for this.',
            'Allow people to walk under raised forks or loads.',
            'Leave the keys in an unattended fork lift truck.',
            'Allow visitors / visiting lorry drivers to operate the lift truck.',
            'Stand behind a reversing fork lift truck.',
          ]}
        />
      </SubSection>

      <SubSection title="5.3 Loads">
        <DoDoNot
          doItems={[
            'Observe floor loading limits – find out the weight of the laden lift truck.',
            'Ensure there is adequate clearance for the lift truck and load, including overhead.',
            'Be sure the load you are lifting is ready to be lifted, for example loads are not caught in adjacent pallets etc.',
            'Check that the load is correctly balanced on the forks.',
            'Lower and raise loads at a safe speed.',
            "Use suitable attachments for lifting unusual or wide loads and follow the manufacturer's instructions.",
            'Check all personnel are at a safe distance before loading or unloading etc.',
            'Ensure you are properly trained, competent and authorised to operate the lift truck with the attachment being used.',
            'When loading / unloading vehicles: ask the lorry driver to clear any debris; check the vehicle engine is turned off, parking brake applied, keys removed; check personnel are at a safe distance; be aware of the potential of falling goods when opening doors; when loading / unloading curtain siders keep the opposite curtain shut.',
          ]}
          doNotItems={[
            'Lift loads greater than the capacity of the lift truck.',
            'Lift / lower a load while the lift truck is in motion.',
            'Move a load that appears unsuitable or unstable (including on a damaged pallet).',
            'Travel with a bulky load which blocks your view.',
            'Travel with a raised load unless the lift truck is designed for this.',
            'Use an attachment unless a competent person, authorised dealer or manufacturer has de-rated the lift truck.',
            'Pick up a load if someone is standing close to it.',
          ]}
        />
      </SubSection>

      <SubSection title="5.4 Slopes">
        <DoDoNot
          doItems={[
            'Travel slowly when going down slopes.',
            'When carrying a load, ensure the forks face uphill when travelling up or down slopes.',
            'Without a load, ensure the forks face downhill when travelling up or down slopes.',
            'Adjust the tilt (where fitted) to suit the gradient and raise the forks to clear the ground.',
          ]}
          doNotItems={[
            'Attempt to turn on or travel across a ramp or a slope.',
            'Leave a lift truck on a gradient except in an emergency, in which case always chock the wheels.',
          ]}
        />
      </SubSection>

      <SubSection title="5.5 Use of Work Platform / Cage">
        <DoDoNot
          doItems={[
            'ENSURE the work platform is secured to the fork lift truck e.g. via locking pin and check it is fully engaged onto the forks.',
            'Prior to raising a work platform: check the ground is level / firm / no obstructions; check no voids (manholes, excavations, ditches); check there are no overhead services / obstructions.',
            'CHECK the parking brake is applied before raising the work platform.',
            'Where applicable, place the transmission in neutral before raising the work platform.',
            'Remain at the controls of the lift truck while the work platform is elevated.',
            'Operate the controls smoothly.',
            'Keep the work platform floor clear of debris and loose objects.',
            'Make certain everyone is clear of the fork lift truck before lowering / raising the work platform.',
            'Wear the work restraint provided when in the man cage (typically a full body harness with lanyard attached to an anchor point).',
            'Cone off the area when working.',
          ]}
          doNotItems={[
            'Leave a fork lift unattended if somebody is in the work platform.',
            'Attempt to climb into or out of the work platform when it is in the raised position.',
            'Lift persons on the fork arms or on a pallet placed on the fork arms.',
            'Drive a fork lift truck when a person is in the work platform / the work platform is in the raised position.',
            'Attempt to touch the lifting mechanism, chains etc whilst the work platform is being raised / the fork lift truck is in operation.',
            'Exceed the safe working load noted on the work platform.',
            'Use the work platform in high winds, fog, heavy rain or lightning or under overhead power lines.',
            'Use ladders, planks, steps or other devices to provide additional reach or gain greater height.',
            'Lean over or sit or climb on the platform railings.',
            'Jump from a raised work platform.',
            'Operate a fork lift truck with a work platform on uneven, loose or sloping surfaces.',
            'Attempt to operate tilt, side shift, chassis / mast levelling, or reach whilst the work platform is elevated.',
          ]}
        />
      </SubSection>

      <SubSection title="5.6 Battery Charging">
        <DoDoNot
          doItems={[
            'Carry out a visual inspection of the battery charger before use. Report any defects.',
            'Wear the PPE provided (eye protection / acid resistant gloves / apron) when working on batteries.',
            'Be careful that any metallic jewellery or tools do not come into contact with the battery charger, terminals etc.',
            'Charge the battery in well ventilated areas. Smoking / naked flames prohibited.',
            'Turn off the ignition switch and all other switches, or otherwise isolate the battery from the circuit.',
            'Ensure that the charger is switched off or disconnected from its power supply before connecting the charging leads.',
            'Ensure connections are secure before switching on.',
            'Switch off the charger before disconnecting the charging leads once charging is complete.',
            'Flush acid burns immediately with clear water.',
            'Charge at the correct rate.',
          ]}
          doNotItems={[
            'Rest tools or metallic objects on the top of a battery.',
            'Remove the "clamp" while the charger is switched on.',
            'Use in wet or damp conditions.',
            'Smoke or use naked flames etc near the equipment / charging area.',
            'Exceed the recommended rate of charging.',
            'Pull or carry the equipment by its power supply lead.',
            'Pull power plugs from sockets by the power cable.',
            'Recharge non-rechargeable batteries.',
            'Use a mobile phone or similar equipment.',
          ]}
        />
      </SubSection>

      <SubSection title="5.7 Refuelling – Changing LPG Cylinders">
        <DoDoNot
          doItems={[
            'Switch off the vehicle engine and remove the key.',
            'Switch off the cylinder and all electric circuits on the vehicle.',
            'Close the cylinder valve before taking the cylinder off truck.',
            'Ensure the valve sealing gasket is in place at all times.',
            'Place the new cylinder on the vehicle with the arrow facing downwards.',
            'Re-couple with the cylinder valve closed.',
            'Check for evidence of leaks (frosting, hissing).',
            'Ensure the used cylinder is stored correctly.',
            'Check the cylinder is firmly secured before operating the vehicle.',
          ]}
          doNotItems={[
            'Smoke or have any ignition sources in vicinity of any aspect of this operation.',
            'Use a mobile phone or similar equipment.',
            'Lift above your capacity, obtain assistance if required to lift cylinders.',
            'Operate the truck without the cylinder firmly secured.',
            'Carry out any aspect of operation with forklift ignition circuit being live.',
          ]}
        />
      </SubSection>

      <SubSection title="5.8 Refuelling with Diesel">
        <DoDoNot
          doItems={[
            'Switch off the vehicle engine and remove the key.',
            'Select the correct fuel / fuel nozzle.',
            'Remain with the vehicle during filling.',
            'Clean up any small spillage with the material provided.',
            'Alert the site manager immediately in the event of a large spillage.',
            'Replace the fuel cap immediately when filling is completed.',
          ]}
          doNotItems={[
            'Smoke or have any ignition sources in vicinity of any aspect of this operation.',
            'Use a mobile phone or similar equipment.',
          ]}
        />
      </SubSection>

      <SubSection title="5.9 Specific Operations – Loading / Unloading Vessels">
        <Paragraph className="mb-2">
          When loading or unloading LPG vessels, adhere to the procedures outlined in the 'Lifting
          Operations and Transport', 'Pyrolysis', and 'Shot Blasting' Method Statements, ensuring
          coordination with other personnel involved.
        </Paragraph>
        <BulletList
          items={[
            'Ensure the area is clear of pedestrians and other traffic.',
            'Position the forklift squarely to the load.',
            'Lift and lower loads smoothly, avoiding swinging.',
            'Ensure the vessel is stable on the forks before moving.',
            'When placing vessels, ensure they are chocked or secured appropriately.',
          ]}
        />
      </SubSection>

      <SubSection title="5.10 When You Have Finished Working / Parking">
        <BulletList
          items={[
            'Park the lift truck on level ground, never on a slope. Use designated areas.',
            'Leave the lift truck with the mast tilted forwards and the forks fully lowered, with the tips on the floor.',
            'Apply the parking brake, select neutral, switch off the engine and remove the key.',
            'Return keys or other activating devices to their place of safe-keeping to prevent unauthorised use.',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="6" title="Personal Protective Equipment (PPE)">
      <Paragraph className="mb-2">
        Appropriate PPE shall be worn at ALL times when operating or working near forklifts. This
        includes, but is not limited to:
      </Paragraph>
      <BulletList
        items={[
          'Hi-visibility jacket / vest (essential for all personnel in forklift operating areas).',
          'Appropriate footwear (rubber soled, steel toe-capped).',
          'Hard hat (especially when working in areas where overhead hazards exist or during lifting operations).',
          'Gloves (suitable for handling loads, chains, etc.).',
          'Eye protection (safety glasses / goggles).',
        ]}
      />
    </Section>

    <Section number="7" title="Training and Competence">
      <BulletList
        items={[
          'All forklift operators must hold a valid and current certificate of training / licence for the specific type of forklift they operate. Evidence of training is held on file.',
          <>
            Initial training will cover:
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Safe operating procedures.</li>
              <li>Pre-use checks and defect reporting.</li>
              <li>Load handling and stability.</li>
              <li>Site traffic rules and pedestrian segregation.</li>
              <li>Emergency procedures.</li>
            </ul>
          </>,
          'Refresher training and re-assessment of competence will be conducted periodically, or following an incident, near miss, or significant change in work practices / equipment.',
          'Only suitably trained and authorised personnel are permitted to operate forklifts.',
        ]}
      />
    </Section>

    <Section number="8" title="Emergency Procedures">
      <BulletList
        items={[
          <>
            <strong>Accident / Collision:</strong>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Immediately stop the forklift.</li>
              <li>Assess the situation, ensure safety of all personnel.</li>
              <li>Render first aid if required.</li>
              <li>Notify the Health and Safety Officer / Supervisor immediately.</li>
              <li>Do not move the forklift or disturb the scene unless absolutely necessary for safety or to prevent further injury.</li>
              <li>Report all details for accident investigation.</li>
            </ul>
          </>,
          <>
            <strong>Forklift Fire:</strong>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Stop the forklift, turn off engine, evacuate immediately.</li>
              <li>Activate nearest fire alarm.</li>
              <li>Only attempt to extinguish if trained and safe to do so with appropriate extinguisher.</li>
              <li>Notify emergency services (999) and management.</li>
            </ul>
          </>,
          <>
            <strong>Overturning:</strong>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li><strong>DO NOT JUMP CLEAR.</strong> Stay in the operator's seat, brace yourself, and lean away from the point of impact.</li>
              <li>Once movement stops, turn off engine (if possible) and await assistance.</li>
              <li>Do not attempt to right the forklift yourself.</li>
            </ul>
          </>,
          <><strong>Spills:</strong> In case of fuel or hydraulic fluid spills, contain immediately, use spill kits, and notify supervisor.</>,
        ]}
      />
    </Section>

    <Section number="9" title="Environmental Considerations">
      <BulletList
        items={[
          'Ensure proper disposal of waste oils, fuels, and maintenance materials in accordance with environmental regulations.',
          'Report any leaks or spills immediately to prevent environmental contamination.',
          'Minimise engine idling to reduce emissions.',
        ]}
      />
    </Section>

    <Section number="10" title="Review">
      <Paragraph>
        This combined Risk Assessment and Safe System of Work will be reviewed at regular intervals
        to ensure it is achieving its aims effectively. It will be reviewed every 24 months by the
        Board of Directors and amended appropriately with careful consideration and consultation
        with all relevant parties. It may also be reviewed following any incident, near miss,
        significant change in forklift type, work environment, or operating procedures.
      </Paragraph>
    </Section>

    <Section number="11" title="Reminder">
      <Paragraph className="font-semibold">
        REMEMBER – DO NOT OPERATE A LIFT TRUCK UNLESS YOU ARE TRAINED AND AUTHORISED TO DO SO. You
        have a duty not only to protect your own health and safety but also to ensure that your
        actions do not put others around you at risk.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default ForkLiftTruckDoc;
